import React from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Select, { type SelectOption } from "../../../components/ui/Select";

interface MaintenanceFiltersProps {
	filters: { status: string; priority: string };
	onFilterChange: (filters: { status?: string; priority?: string }) => void;
	requestCount: number;
}

const STATUS_OPTIONS: SelectOption[] = [
	{ value: "", label: "All Status" },
	{ value: "pending", label: "Pending" },
	{ value: "in_progress", label: "In Progress" },
	{ value: "completed", label: "Completed" },
	{ value: "cancelled", label: "Cancelled" },
	{ value: "on_hold", label: "On Hold" },
];

const PRIORITY_OPTIONS: SelectOption[] = [
	{ value: "", label: "All Priorities" },
	{ value: "low", label: "Low" },
	{ value: "medium", label: "Medium" },
	{ value: "high", label: "High" },
	{ value: "urgent", label: "Urgent" },
];

const MaintenanceFilters: React.FC<MaintenanceFiltersProps> = ({
	filters,
	onFilterChange,
	requestCount,
}) => {
	const clearFilters = () => {
		onFilterChange({ status: "", priority: "" });
	};

	const hasActiveFilters = filters.status !== "" || filters.priority !== "";

	return (
		<div className="bg-card border border-border rounded-lg card-shadow">
			<div className="p-3 sm:p-4 border-b border-border">
				<div className="flex items-center justify-between">
					<h3 className="text-sm sm:text-base font-semibold text-foreground">
						Filters
					</h3>
					{hasActiveFilters && (
						<Button
							variant="ghost"
							size="sm"
							onClick={clearFilters}
							className="text-xs h-7 px-2"
						>
							Clear all
						</Button>
					)}
				</div>

				<p className="text-xs sm:text-sm text-muted-foreground mt-1">
					{requestCount} request{requestCount !== 1 ? "s" : ""} found
				</p>
			</div>

			<div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
				{/* Status Filter */}
				<div>
					<label className="block text-sm font-medium text-foreground mb-2">
						Status
					</label>
					<Select
						value={filters.status}
						options={STATUS_OPTIONS}
						onValueChange={(value) =>
							onFilterChange({ status: value as string })
						}
					/>
				</div>

				{/* Priority Filter */}
				<div>
					<label className="block text-sm font-medium text-foreground mb-2">
						Priority
					</label>
					<Select
						value={filters.priority}
						options={PRIORITY_OPTIONS}
						onValueChange={(value) =>
							onFilterChange({ priority: value as string })
						}
					/>
				</div>

				{/* Quick Filter Buttons */}
				<div>
					<label className="block text-sm font-medium text-foreground mb-2">
						Quick Filters
					</label>
					<div className="space-y-2">
						<Button
							variant={filters.status === "pending" ? "default" : "outline"}
							size="sm"
							onClick={() =>
								onFilterChange({
									status: filters.status === "pending" ? "" : "pending",
								})
							}
							className="w-full justify-start text-xs"
						>
							<Icon name="Clock" size={14} className="mr-2" />
							Pending Requests
						</Button>

						<Button
							variant={filters.priority === "urgent" ? "default" : "outline"}
							size="sm"
							onClick={() =>
								onFilterChange({
									priority: filters.priority === "urgent" ? "" : "urgent",
								})
							}
							className="w-full justify-start text-xs"
						>
							<Icon name="AlertTriangle" size={14} className="mr-2" />
							Urgent Priority
						</Button>

						<Button
							variant={filters.status === "in_progress" ? "default" : "outline"}
							size="sm"
							onClick={() =>
								onFilterChange({
									status: filters.status === "in_progress" ? "" : "in_progress",
								})
							}
							className="w-full justify-start text-xs"
						>
							<Icon name="Wrench" size={14} className="mr-2" />
							In Progress
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MaintenanceFilters;
